import clsx from 'clsx';
import { Add, ContentCopyOutlined, DeleteOutline } from '@mui/icons-material';

import { Button } from '../../../../../../../components';
import { copyToClipboard } from '../../../../../../../components/Copy';

export const EMPTY_KEY_ERROR = 'Key is required';
export const EMPTY_VALUE_ERROR = 'Value is required';
export const DUPLICATE_KEY_ERROR = 'Key must be unique';
export const DUPLICATE_VALUE_ERROR = 'Value must be unique';

export const REMOVE_TOOLTIP = 'Remove';
export const LOCKED_REMOVE_TOOLTIP = 'This field cannot be removed';
export const COPY_KEY_TOOLTIP = 'Copy key';

export const DEFAULT_MAX_ITEMS_HEIGHT_CLASSNAME = 'max-h-[28rem]';

type EntryWithId = { id: string };

export const getDuplicateTrimmedValues = (
  values: ReadonlyArray<string>
): Set<string> => {
  const counts = new Map<string, number>();

  values.forEach(value => {
    const trimmed = value.trim();
    if (!trimmed) return;
    counts.set(trimmed, (counts.get(trimmed) ?? 0) + 1);
  });

  const duplicates = Array.from(counts.entries())
    .filter(([, count]) => count > 1)
    .map(([value]) => value);

  return new Set(duplicates);
};

export const removeEntryById = <T extends EntryWithId>(
  entries: T[],
  id: string
): T[] => entries.filter(entry => entry.id !== id);

export const updateEntryById = <T extends EntryWithId>(
  entries: T[],
  id: string,
  patch: Partial<T>
): T[] =>
  entries.map(entry => (entry.id === id ? { ...entry, ...patch } : entry));

export const FieldLabel: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <span className="text-tiny text-secondary uppercase tracking-wide">
    {children}
  </span>
);

export const FieldError: React.FC<{ message?: string }> = ({ message }) => {
  if (!message) return null;
  return <span className="text-danger text-small italic">{message}</span>;
};

export const EmptyHint: React.FC<{ message?: string }> = ({ message }) => {
  if (!message) return null;
  return <span className="text-secondary text-small italic">{message}</span>;
};

type RemoveEntryButtonProps = {
  onClick: () => void;
  disabled?: boolean;
  tooltip?: string;
};

export const RemoveEntryButton: React.FC<RemoveEntryButtonProps> = ({
  onClick,
  disabled = false,
  tooltip,
}) => (
  <Button
    size="small"
    type="contrast"
    icon={<DeleteOutline fontSize="medium" color="error" />}
    onClick={onClick}
    tooltip={tooltip ?? (disabled ? LOCKED_REMOVE_TOOLTIP : REMOVE_TOOLTIP)}
    disabled={disabled}
    className="!py-[0.7rem]"
  />
);

type CopyEntryButtonProps = {
  text: string;
  disabled?: boolean;
  tooltip?: string;
};

export const CopyEntryButton: React.FC<CopyEntryButtonProps> = ({
  text,
  disabled = false,
  tooltip,
}) => (
  <Button
    size="small"
    type="contrast"
    icon={<ContentCopyOutlined fontSize="medium" color="primary" />}
    onClick={() => copyToClipboard(text)}
    tooltip={tooltip ?? COPY_KEY_TOOLTIP}
    disabled={disabled || !text}
    className="!py-[0.7rem]"
  />
);

type AddEntryButtonProps = {
  label: string;
  onClick: () => void;
};

export const AddEntryButton: React.FC<AddEntryButtonProps> = ({
  label,
  onClick,
}) => (
  <Button
    size="small"
    type="outline"
    icon={<Add fontSize="small" />}
    label={label}
    onClick={onClick}
    className="self-start"
  />
);

type EntriesScrollAreaProps = {
  hasEntries: boolean;
  maxHeightClassName: string;
  gapClassName?: string;
  children: React.ReactNode;
};

export const EntriesScrollArea: React.FC<EntriesScrollAreaProps> = ({
  hasEntries,
  maxHeightClassName,
  gapClassName = 'gap-y-3',
  children,
}) => (
  <div
    className={clsx(
      'flex flex-col w-full overflow-y-auto pr-1',
      gapClassName,
      hasEntries && maxHeightClassName
    )}
  >
    {children}
  </div>
);
